import type { Participant } from '@seen/core/models/patients/participant'
import { getLanguageDisplay } from '@seen/core/models/profile'
import type { ExtendedRelatedPerson } from '@seen/core/models/relatedPerson'
import { cn, TypographyDetail } from '@seen/ui'
import type { JSX } from 'react'
import { LanguagesMultiSelect } from '#src/components/patient/detail-content/contact-tab/LanguagesMultiSelect.tsx'
import { PreferredLanguageStar } from '#src/components/patient/detail-content/contact-tab/PreferredLanguageStar.tsx'

interface LanguagesDisplayProps {
  profile: Participant | ExtendedRelatedPerson
}

export function LanguagesDisplay({
  profile,
}: LanguagesDisplayProps): JSX.Element {
  // A long language list wraps onto multiple lines instead of truncating;
  // min-w-0 stops the non-wrapping trigger text from widening the card past
  // its column.
  return (
    <div className="group flex items-center gap-1">
      <div className="grid min-w-0 flex-1">
        <TypographyDetail className="leading-none font-semibold text-zinc-950">
          Languages
        </TypographyDetail>
        <div className="py-1.5">
          <LanguagesMultiSelect
            profile={profile}
            variant="underline"
            className="w-full"
            // Names come from the code (getLanguageDisplay), not the stored
            // display — imports sometimes persist the raw code ("zh", "en")
            // as the display. Wraps instead of truncating; min-w-0 stops the
            // trigger's min-content width from widening the card past its
            // column.
            renderTrigger={(value) => (
              <span className="min-w-0 flex-1 text-left whitespace-normal">
                {value.length === 0 ? (
                  <span className="text-muted-foreground">Add language</span>
                ) : (
                  value.map(({ code, preferred }, index) => (
                    <span
                      key={code}
                      className={cn(
                        preferred ? 'text-foreground' : 'text-muted-foreground'
                      )}
                    >
                      {getLanguageDisplay(code)}
                      {preferred && <PreferredLanguageStar />}
                      {index < value.length - 1 ? ', ' : ''}
                    </span>
                  ))
                )}
              </span>
            )}
          />
        </div>
      </div>
    </div>
  )
}
